import { useCallback, useRef, useState } from 'react';
import {
  ADD_TO_YARUTA_COPY,
  type ReadOnDay,
} from '@/constants/addToYarutaCopy';
import {
  classifyUploadFile,
  prepareImage,
  sha256Hex,
  UploadPrepError,
} from '@/lib/uploadPrep';
import { toDateKeyTz, resolveTimezone } from '@/lib/tz';
import {
  createUpload,
  UploadError,
  type PreparedUpload,
  type UploadRefusal,
} from '@/services/uploads.service';
import { triggerProcessing } from '@/services/email.service';
import { useToastStore } from '@/stores/toastStore';
import { useUserStore } from '@/stores/userStore';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';

/**
 * How long a check-before-reading toast stays. These only tell: nothing was
 * read or counted, so they close on their own (YAR-556 global rules).
 */
export const PRE_READ_TOAST_MS = 6000;

/** Refusals whose copy is a headline + body pair in the COPY table. */
const REFUSAL_COPY = ADD_TO_YARUTA_COPY.refusal;
type RefusalWithCopy = keyof typeof REFUSAL_COPY;

function hasRefusalCopy(code: UploadRefusal): code is RefusalWithCopy {
  return code in REFUSAL_COPY;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days between two YYYY-MM-DD keys (b − a). */
function daysBetween(a: string, b: string): number {
  return Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS,
  );
}

/**
 * When the earlier copy was read (UX kit §11): the same calendar day is
 * `today`, the day before `yesterday`, the five days before that a weekday
 * (`Monday`), anything older a short date (`Oct 26`). Days are counted in
 * the timezone given — the profile's, which the shell uses for every other
 * day boundary; the card subject is built server-side in the household's.
 */
export function readOnDay(
  readOnIso: string,
  now: Date,
  timezone: string | null,
): ReadOnDay {
  const timeZone = resolveTimezone(timezone);
  const readOn = new Date(readOnIso);
  const readOnKey = toDateKeyTz(readOnIso, timezone);
  const nowKey = toDateKeyTz(now.toISOString(), timezone);
  const days = daysBetween(readOnKey, nowKey);
  if (days <= 0) return { kind: 'today' };
  if (days === 1) return { kind: 'yesterday' };
  if (days <= 6) {
    return {
      kind: 'weekday',
      label: new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'long',
      }).format(readOn),
    };
  }
  return {
    kind: 'date',
    label: new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      // The year only when it isn't this year (UX kit §11).
      ...(readOnKey.slice(0, 4) !== nowKey.slice(0, 4) && {
        year: 'numeric',
      }),
    }).format(readOn),
  };
}

/**
 * Add to Yaruta (YAR-556): one file from the sheet to `uploads-create`.
 *
 * Every check before reading ends in a top toast that only tells (6 s):
 * the browser's own checks first (kind, size, HEIC decode), then the
 * server's (page count, already added, the daily read limit). On success
 * it only kicks the worker; the bottom status toast narrates from there.
 */
export interface UseAddUploadOptions {
  /**
   * The upload went through (its row exists, RECEIVED). The shell re-reads
   * the status feed at once, so "Reading your photo" never waits on Realtime
   * or the idle poll.
   */
  onUploaded?: () => void;
}

export function useAddUpload(
  householdId: string | null,
  { onUploaded }: UseAddUploadOptions = {},
): {
  addFile(file: File): Promise<void>;
  busy: boolean;
} {
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;
  const [busy, setBusy] = useState(false);
  const timezone = useUserStore((s) => s.timezone);

  const addFile = useCallback(
    async (file: File) => {
      if (!householdId) return;
      const { addToast } = useToastStore.getState();
      const tell = (
        variant: 'error' | 'info',
        headline: string,
        description?: string,
      ) => {
        addToast(
          variant,
          headline,
          description === undefined
            ? { durationMs: PRE_READ_TOAST_MS }
            : { description, durationMs: PRE_READ_TOAST_MS },
        );
      };
      const refuse = (code: RefusalWithCopy) =>
        tell('error', REFUSAL_COPY[code].headline, REFUSAL_COPY[code].body);
      // The repo's generic failure line (Review's Confirm all), without its
      // Confirm-all body.
      const fail = () => tell('error', REVIEW_COPY.failHeadline);

      setBusy(true);
      try {
        const classification = await classifyUploadFile(file);
        if (!classification.ok) {
          refuse(classification.refusal);
          return;
        }
        const { kind, sniffed } = classification;

        const originalSha256 = await sha256Hex(file);
        let body: Pick<PreparedUpload, 'blob' | 'ext'>;
        if (sniffed === 'pdf') {
          body = { blob: file, ext: 'pdf' };
        } else {
          try {
            const prepared = await prepareImage(file, sniffed);
            body = { blob: prepared.blob, ext: prepared.ext };
          } catch (err) {
            if (err instanceof UploadPrepError) refuse(err.code);
            else fail();
            return;
          }
        }

        try {
          await createUpload(householdId, {
            ...body,
            originalSha256,
            originalBytes: file.size,
            originalMime: file.type,
            filename: file.name,
          });
        } catch (err) {
          if (!(err instanceof UploadError)) {
            fail();
          } else if (err.code === 'already_added') {
            tell(
              'info',
              ADD_TO_YARUTA_COPY.alreadyAdded(kind),
              err.readOn
                ? ADD_TO_YARUTA_COPY.alreadyAddedBody(
                    readOnDay(err.readOn, new Date(), timezone),
                  )
                : undefined,
            );
          } else if (err.code === 'vision_limit') {
            tell(
              'info',
              ADD_TO_YARUTA_COPY.visionLimit.headline,
              ADD_TO_YARUTA_COPY.visionLimit.body,
            );
          } else if (hasRefusalCopy(err.code)) {
            refuse(err.code);
          } else {
            fail();
          }
          return;
        }

        onUploadedRef.current?.();
        triggerProcessing().catch(() => {});
      } catch {
        // Reading the file's bytes failed (hashing, sniffing).
        fail();
      } finally {
        setBusy(false);
      }
    },
    [householdId, timezone],
  );

  return { addFile, busy };
}
